import { apiClient, type Paginated } from '@/core/api';
import type { Course, CourseInput, Enrollment, Lesson, LessonInput } from './types';

export const BASE = '/lms/courses/';
const LESSONS = '/lms/lessons/';
const ENROLLMENTS = '/lms/enrollments/';

export interface CourseListParams {
  page?: number;
  search?: string;
  is_published?: boolean;
  /** DRF `OrderingFilter` value, e.g. `title` or `-created`. */
  ordering?: string;
}

export async function listCourses(params: CourseListParams = {}): Promise<Paginated<Course>> {
  const { data } = await apiClient.get<Paginated<Course>>(BASE, { params });
  return data;
}

export async function getCourse(id: string | number): Promise<Course> {
  const { data } = await apiClient.get<Course>(`${BASE}${id}/`);
  return data;
}

export async function createCourse(input: CourseInput): Promise<Course> {
  const { data } = await apiClient.post<Course>(BASE, input);
  return data;
}

export async function updateCourse(
  id: string | number,
  input: Partial<CourseInput>,
): Promise<Course> {
  const { data } = await apiClient.patch<Course>(`${BASE}${id}/`, input);
  return data;
}

export async function deleteCourse(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

export async function listLessons(course: number): Promise<Paginated<Lesson>> {
  const { data } = await apiClient.get<Paginated<Lesson>>(LESSONS, { params: { course } });
  return data;
}

export async function createLesson(input: LessonInput): Promise<Lesson> {
  const { data } = await apiClient.post<Lesson>(LESSONS, input);
  return data;
}

/** The current user's own enrollments. */
export async function listMyEnrollments(): Promise<Paginated<Enrollment>> {
  const { data } = await apiClient.get<Paginated<Enrollment>>(ENROLLMENTS);
  return data;
}

export async function enroll(course: number): Promise<Enrollment> {
  const { data } = await apiClient.post<Enrollment>(ENROLLMENTS, { course });
  return data;
}

/** POST /lms/enrollments/{id}/complete-lesson/ → returns the updated enrollment. */
export async function completeLesson(enrollmentId: number, lesson: number): Promise<Enrollment> {
  const { data } = await apiClient.post<Enrollment>(
    `${ENROLLMENTS}${enrollmentId}/complete-lesson/`,
    { lesson },
  );
  return data;
}
